import { z } from "zod";
const translationPath =
  "page.4uSalesContract.registers.salesContractPayer.errors.";

const legalEntitySchema = z.object({
  id: z.string(),
  identifier: z.string().optional().nullable(),
  corporateName: z.string().optional().nullable(),
  tradingName: z.string().optional().nullable(),
  document: z.string().optional().nullable(),
  dataOrigin: z.string().optional().nullable(),
});

const individualSchema = z.object({
  identifier: z.string(),
  individualName: z.string().optional().nullable(),
  document: z.string().optional().nullable(),
  establishmentId: z.string().optional().nullable(),
});

const situationSchema = z.object(
  {
    id: z.string(),
    name: z.string().optional().nullable(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "situation" }
);
const addressTypeSchema = z.object(
  {
    id: z.string(),
    name: z.string().optional().nullable(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "address_type" }
);

const bilingMethodSchema = z.object(
  {
    id: z.string(),
    name: z.string().optional().nullable(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "biling_method" }
);

export const salesContractPayerSchema = z
  .object({
    id: z.string().optional(),
    payerTitle: z.string().min(1, translationPath + "payer_title"),
    addressType: addressTypeSchema,
    integrationId: z.string().nullish(),
    code: z.string().nullish(),
    entity: z.enum(["QPF", "QPJ"]).optional(),
    situation: situationSchema.nullish(),
    idSalesContractPayerFinantial: z.string().optional().nullish(),
    initialDate: z.date({
      invalid_type_error: translationPath + "initial_date",
    }),
    endDate: z.date({ invalid_type_error: translationPath + "end_date" }),
    monthlyPaymentDueDate: z
      .string()
      .min(1, translationPath + "monthly_payment_due_date"),
    mail: z.string().nullish(),
    phone: z.string().nullish(),
    whatsappNumber: z.string().nullish(),
    enable: z.boolean(),
    observation: z.string().nullish(),
    bilingMethod: bilingMethodSchema.nullish(),
    individual: individualSchema.optional().nullish(),
    legalEntity: legalEntitySchema.optional().nullish(),
  })
  .superRefine((value, context) => {
    const {
      entity,
      individual,
      legalEntity,
      bilingMethod,
      initialDate,
      endDate,
    } = value;

    if (entity === "QPF" && !individual) {
      context.addIssue({
        code: "custom",
        path: ["individual"],
        message: translationPath + "individual_person",
      });
    } else if (entity === "QPJ" && !legalEntity) {
      context.addIssue({
        code: "custom",
        path: ["legalEntity"],
        message: translationPath + "legal_entity",
      });
    }

    if (endDate < initialDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["initialDate"],
        message: translationPath + "date",
      });
    }

    if (!bilingMethod) {
      context.addIssue({
        code: "custom",
        path: ["bilingMethod"],
        message: translationPath + "biling_method",
      });
    }

    value.entity = undefined;
  });

export type TSalesContractPayerForm = z.infer<typeof salesContractPayerSchema>;
